/**
 * objectCreationCore — single-click object creation, extracted from
 * useObjectCreation so BOTH the JointJS paper path (flag off) and the Canvas
 * overlay path (flag on) create objects with identical logic.
 *
 * Handles the single-click kinds (label / icon / colored-rect). Multi-click
 * kinds (line / polygon) return null and are handled by the caller.
 */

import { dia, shapes } from '@joint/core'
import { ObjectType, LayerTypeMapping } from '../../../map-editor-shared/store/objectTypeStore'
import { generateObjectName, resolveConventionLayer } from '../../../map-editor-shared/lib/objectNameGenerator'
import { measureLabelBox, LABEL_FONT_FAMILY } from './labelTextMetrics'

/** 한글 displayName → canonical layer key (mapping 없이 곧장 placement 한 경우 폴백). */
export const DISPLAY_NAME_TO_LAYER_KEY: Record<string, string> = {
  '조명 CCTV': 'p-lightCctv',
  '단방향 CCTV': 'p-warningCctv',
  '전기 충전기': 'c-carCharger',
}

/** placement 시 타입별 고정 크기 (도면 좌표 단위, m 가정). */
export const FIXED_OBJECT_SIZE_BY_LAYER: Record<string, number> = {
  'c-carCharger': 9.25,
}

export interface SingleClickCtx {
  mappings: LayerTypeMapping[]
  defaultObjectSize: number
}

/**
 * Create a single-click object at world coords (x, y), add it to the graph, and
 * return it. Returns null for multi-click kinds (line / polygon), which the
 * caller draws interactively.
 */
export function createSingleClickObject(
  graph: dia.Graph,
  selectedType: ObjectType,
  x: number,
  y: number,
  ctx: SingleClickCtx
): dia.Cell | null {
  const { mappings, defaultObjectSize } = ctx

  const isIconUrl = selectedType.icon && (
    selectedType.icon.startsWith('/') ||
    selectedType.icon.startsWith('http') ||
    selectedType.icon.startsWith('data:image/')
  )
  const isColorCode = selectedType.icon && selectedType.icon.startsWith('#')
  const typeColor = isColorCode ? selectedType.icon : (selectedType.color || '#808080')
  const kind = selectedType.kind || 'asset'

  const resolveLayerAndName = () => {
    const conventionLayer = resolveConventionLayer(selectedType.id, mappings)
    const layerName = conventionLayer
      || DISPLAY_NAME_TO_LAYER_KEY[selectedType.name]
      || selectedType.name
    const objectName = generateObjectName(
      selectedType.id, graph, mappings, layerName, selectedType.name
    )
    return { layerName, objectName }
  }

  if (kind === 'label') {
    const fontSize = selectedType.fontSize ?? 12
    const textColor = selectedType.color || '#ffffff'
    const { layerName, objectName } = resolveLayerAndName()
    const { width: labelWidth, height: labelHeight } = measureLabelBox(objectName, fontSize)

    const labelRect = new shapes.standard.Rectangle()
    labelRect.position(x - labelWidth / 2, y - labelHeight / 2)
    labelRect.resize(labelWidth, labelHeight)
    labelRect.attr({
      body: { fill: 'transparent', stroke: 'none', strokeWidth: 0 },
      label: {
        text: objectName,
        fill: textColor,
        fontSize,
        fontFamily: LABEL_FONT_FAMILY,
        textAnchor: 'middle',
        textVerticalAnchor: 'middle',
      },
    })
    labelRect.set('data', {
      typeId: selectedType.id,
      type: 'point',
      kind: 'label',
      layer: layerName,
      objectType: selectedType.name,
      properties: { name: objectName },
    })
    labelRect.prop('objectTypeId', selectedType.id)
    graph.addCell(labelRect)
    return labelRect
  }

  if (kind === 'line') {
    // Multi-click polyline — handled interactively by the caller.
    return null
  }

  if (isIconUrl && selectedType.icon) {
    const conventionEarly = resolveConventionLayer(selectedType.id, mappings)
    const earlyLayerKey =
      conventionEarly ||
      DISPLAY_NAME_TO_LAYER_KEY[selectedType.name] ||
      selectedType.name
    const defaultSize = FIXED_OBJECT_SIZE_BY_LAYER[earlyLayerKey] ?? defaultObjectSize
    const image = new shapes.standard.Image()
    image.position(x - defaultSize / 2, y - defaultSize / 2)
    image.resize(defaultSize, defaultSize)
    image.attr({
      image: { xlinkHref: selectedType.icon, opacity: 0.9 },
      label: {
        text: '',
        fill: '#ffffff',
        fontSize: 10,
        refY: defaultSize + 5,
        refX: '50%',
        textAnchor: 'middle',
      },
    })
    const conventionLayer = resolveConventionLayer(selectedType.id, mappings)
    const layerName = conventionLayer
      || DISPLAY_NAME_TO_LAYER_KEY[selectedType.name]
      || selectedType.name
    const objectName = generateObjectName(selectedType.id, graph, mappings, layerName, selectedType.name)
    image.set('data', {
      typeId: selectedType.id,
      type: 'point',
      layer: layerName,
      objectType: selectedType.name,
      properties: { name: objectName },
    })
    image.prop('objectTypeId', selectedType.id)
    graph.addCell(image)
    return image
  }

  if (typeColor && !isIconUrl) {
    const defaultSize = defaultObjectSize
    const typeName = selectedType.name.toLowerCase()
    const isParkingType = typeName.includes('주차') || typeName.includes('parking')
    const width = isParkingType ? defaultSize : defaultSize
    const height = isParkingType ? defaultSize * 2 : defaultSize

    const rect = new shapes.standard.Rectangle()
    rect.position(x - width / 2, y - height / 2)
    rect.resize(width, height)
    rect.attr({
      body: { fill: typeColor, fillOpacity: 0.8, stroke: typeColor, strokeWidth: 2, rx: 0, ry: 0 },
      label: {
        text: '',
        fill: '#ffffff',
        fontSize: 10,
        refY: height + 5,
        refX: '50%',
        textAnchor: 'middle',
      },
    })
    const conventionLayer = resolveConventionLayer(selectedType.id, mappings)
    const layerName = conventionLayer
      || DISPLAY_NAME_TO_LAYER_KEY[selectedType.name]
      || selectedType.name
    const objectName = generateObjectName(selectedType.id, graph, mappings, layerName, selectedType.name)
    rect.set('data', {
      typeId: selectedType.id,
      type: 'polygon',
      layer: layerName,
      vertices: [
        [x - width / 2, y - height / 2],
        [x + width / 2, y - height / 2],
        [x + width / 2, y + height / 2],
        [x - width / 2, y + height / 2],
      ] as [number, number][],
      originalPosition: { x: x - width / 2, y: y - height / 2 },
      properties: { name: objectName },
    })
    rect.prop('objectTypeId', selectedType.id)
    graph.addCell(rect)
    return rect
  }

  // Polygon (multi-click) — handled interactively by the caller.
  return null
}

export type DrawPoint = { x: number; y: number }

/**
 * Finish an open polyline from the drawn points. Returns null if fewer than 2
 * points (caller cancels the draw). Logic mirrors useObjectCreation's line finish.
 */
export function finishLineObject(
  graph: dia.Graph,
  selectedType: ObjectType,
  points: DrawPoint[],
  ctx: SingleClickCtx
): dia.Cell | null {
  if (points.length < 2) return null
  const { mappings } = ctx

  const minX = points.reduce((m, p) => Math.min(m, p.x), Infinity)
  const minY = points.reduce((m, p) => Math.min(m, p.y), Infinity)
  const maxX = points.reduce((m, p) => Math.max(m, p.x), -Infinity)
  const maxY = points.reduce((m, p) => Math.max(m, p.y), -Infinity)

  const strokeColor = selectedType.strokeColor || selectedType.color || '#3b82f6'
  const pathData = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x - minX} ${p.y - minY}`)
    .join(' ')

  const line = new shapes.standard.Path()
  line.position(minX, minY)
  line.resize(Math.max(maxX - minX, 1), Math.max(maxY - minY, 1))
  line.attr({
    body: {
      d: pathData,
      fill: 'none',
      stroke: strokeColor,
      strokeWidth: selectedType.strokeWidth ?? 2,
      opacity: selectedType.opacity ?? 0.9,
    },
  })
  const conventionLayer = resolveConventionLayer(selectedType.id, mappings)
  const layerName = conventionLayer
    || DISPLAY_NAME_TO_LAYER_KEY[selectedType.name]
    || selectedType.name
  const objectName = generateObjectName(selectedType.id, graph, mappings, layerName, selectedType.name)
  line.set('data', {
    typeId: selectedType.id,
    type: 'line',
    kind: 'line',
    layer: layerName,
    objectType: selectedType.name,
    vertices: points.map(p => [p.x, p.y] as [number, number]),
    originalPosition: { x: minX, y: minY },
    properties: { name: objectName },
  })
  line.prop('objectTypeId', selectedType.id)
  graph.addCell(line)
  return line
}

/**
 * Finish a filled polygon from the drawn points. Logic mirrors
 * useObjectCreation's polygon finish.
 */
export function finishPolygonObject(
  graph: dia.Graph,
  selectedType: ObjectType,
  points: DrawPoint[],
  ctx: SingleClickCtx
): dia.Cell {
  const { mappings } = ctx
  const polygon = new shapes.standard.Polygon()
  const pointsStr = points.map(p => `${p.x},${p.y}`).join(' ')
  const color = selectedType.color || '#3b82f6'

  polygon.attr({
    body: {
      refPoints: pointsStr,
      fill: color,
      fillOpacity: 0.8,
      stroke: color,
      strokeWidth: 2,
      opacity: 0.8,
    },
    label: {
      text: '',
      fill: '#ffffff',
      fontSize: 12,
      refY: '50%',
      refX: '50%',
      textAnchor: 'middle',
      textVerticalAnchor: 'middle',
    },
  })
  const conventionLayer = resolveConventionLayer(selectedType.id, mappings)
  const layerName = conventionLayer || selectedType.name
  const objectName = generateObjectName(selectedType.id, graph, mappings, null, selectedType.name)
  const polyMinX = points.reduce((m, p) => Math.min(m, p.x), Infinity)
  const polyMinY = points.reduce((m, p) => Math.min(m, p.y), Infinity)
  polygon.set('data', {
    typeId: selectedType.id,
    type: 'polygon',
    layer: layerName,
    vertices: points.map(p => [p.x, p.y] as [number, number]),
    originalPosition: { x: polyMinX, y: polyMinY },
    properties: { name: objectName },
  })
  polygon.prop('objectTypeId', selectedType.id)
  graph.addCell(polygon)
  return polygon
}
